import React, { useEffect, useState } from 'react';
import { Typography } from '@mui/material';
import { Divider, Select, Spin } from 'antd';

import Modal from '../../../../../../../components/uiComponent/modal/index';
import WarningIcon from '../../../../../../../assets/icons/warning-modal-icon.svg';
import { ModalWrapper } from '../../../../../../purchase-order/po-warehouse-details/custom-drawer/style.js';

const { Option } = Select;

const MAX_NAME_LENGTH = 255;

// Mirrors MergeWorkOrdersModal.js's structure/conventions (same Modal/ModalWrapper, same
// heading/spacing style). The destination picker itself is a single search-or-create Select,
// modeled on restock/AddToPoModal.jsx's "Purchase Order" dropdown: typing searches existing
// Draft work orders, and a "+ Create new work order" option appears when there's no exact
// match, letting the user create one with that typed name instead of toggling a separate mode.
// Fully presentational/controlled - no dispatch here, matching MergeWorkOrdersModal's own convention.
const MoveItemsModal = ({
  open,
  onClose,
  onConfirm,
  selectedItems = [],
  eligibleTargetWorkOrders = [],
  fetchingTargets = false,
  loading = false,
}) => {
  const [targetWorkOrderId, setTargetWorkOrderId] = useState(undefined);
  const [newWorkOrderName, setNewWorkOrderName] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!open) return;
    setTargetWorkOrderId(undefined);
    setNewWorkOrderName('');
    setSearch('');
  }, [open]);

  const trimmedSearch = search.trim();
  const exactMatch = trimmedSearch
    ? eligibleTargetWorkOrders.some(
      (wo) => (wo.order_name || `#${wo.id}`).toLowerCase() === trimmedSearch.toLowerCase()
    )
    : false;
  const showCreateNewOption = !!trimmedSearch && !exactMatch && trimmedSearch.length <= MAX_NAME_LENGTH;

  const confirmDisabled = loading || (!targetWorkOrderId && !newWorkOrderName.trim());

  const handleConfirm = () => {
    onConfirm({
      targetWorkOrderId: newWorkOrderName ? null : targetWorkOrderId,
      orderName: newWorkOrderName ? newWorkOrderName.trim() : null,
    });
  };

  const handleCreateNew = () => {
    setNewWorkOrderName(trimmedSearch);
    setTargetWorkOrderId(undefined);
  };

  const handleChange = () => {
    setNewWorkOrderName('');
    setSearch('');
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      onHide={onClose}
      onSave={handleConfirm}
      closeText="Cancel"
      saveText={loading ? 'Moving...' : (newWorkOrderName ? 'Create & Move' : 'Move')}
      header="Move Items"
      className="delete-confirmation-modal merge-wo-modal"
      closeButton={true}
      buttonDisabled={confirmDisabled}
    >
      <ModalWrapper>
        <div className="delete-modal-wrapper">
          <img src={WarningIcon} alt="WarningIcon" title="WarningIcon" />
          <Typography
            component="h3"
            sx={{
              fontSize: { xs: 18, sm: 20 },
              fontWeight: 700,
              mt: 2,
              textAlign: 'center',
            }}
          >
            Move {selectedItems.length} Item{selectedItems.length === 1 ? '' : 's'}?
          </Typography>
          <p style={{ marginTop: 12, marginBottom: 8, fontSize: 13, lineHeight: 1.5 }}>
            Selected items will be removed from this work order. Both this work order and the
            destination must be Draft.
          </p>
          {selectedItems.some(item => Number(item.multi_sku_box_count) > 0) && (
            <p style={{ marginTop: 4, marginBottom: 8, fontSize: 13, lineHeight: 1.5, color: '#B54708' }}>
              One or more selected SKUs are part of multi-SKU box(es). Those box(es) will be
              removed - including any other SKUs packed in them that aren't being moved - and
              will need to be repacked.
            </p>
          )}

          <div style={{ width: '100%', alignSelf: 'stretch', textAlign: 'left', marginTop: 16 }}>
            <Typography
              component="div"
              sx={{ fontSize: 13, fontWeight: 600, color: '#1B2559', mb: 1, textAlign: 'left' }}
            >
              Destination work order
            </Typography>

            {newWorkOrderName ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  border: '1px solid #C7D7FE',
                  background: '#F5F8FF',
                  borderRadius: 6,
                  padding: '10px 12px',
                }}
              >
                <span style={{ fontSize: 13, color: '#1B2559' }}>
                  <span style={{ fontWeight: 600 }}>New Work Order:</span> {newWorkOrderName}
                </span>
                <span
                  role="button"
                  tabIndex={0}
                  onClick={handleChange}
                  style={{ fontSize: 13, color: '#4C6FFF', cursor: 'pointer', fontWeight: 600, flexShrink: 0, marginLeft: 8 }}
                >
                  Change
                </span>
              </div>
            ) : (
              <Select
                showSearch
                allowClear
                style={{ width: '100%' }}
                size="large"
                placeholder="Search a Draft work order or type a new name"
                value={targetWorkOrderId}
                onChange={(val) => setTargetWorkOrderId(val || undefined)}
                onSearch={setSearch}
                disabled={loading}
                filterOption={(input, option) =>
                  (option?.children || '').toLowerCase().includes(input.toLowerCase())
                }
                getPopupContainer={(trigger) => trigger.parentNode}
                dropdownStyle={{ zIndex: 1100 }}
                dropdownRender={(menu) => (
                  <Spin spinning={fetchingTargets} size="small">
                    {menu}
                    {showCreateNewOption && (
                      <div
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={handleCreateNew}
                        style={{
                          padding: '8px 12px',
                          borderTop: '1px solid #E5E7EB',
                          color: '#4C6FFF',
                          fontWeight: 600,
                          fontSize: 13,
                          cursor: 'pointer',
                        }}
                      >
                        + Create new work order: &quot;{trimmedSearch}&quot;
                      </div>
                    )}
                  </Spin>
                )}
                notFoundContent={
                  fetchingTargets
                    ? <Spin size="small" />
                    : 'No other Draft work orders available. Type a name to create one.'
                }
              >
                {eligibleTargetWorkOrders.map((wo) => (
                  <Option key={wo.id} value={wo.id}>
                    {(wo.order_name || `#${wo.id}`) + ` (${wo.skus_count || 0} SKUs)`}
                  </Option>
                ))}
              </Select>
            )}
          </div>

          <Divider style={{ margin: '20px 0 12px', borderColor: '#E5E7EB' }} />

          <div style={{ width: '100%', alignSelf: 'stretch', textAlign: 'left' }}>
            <Typography
              component="div"
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: '#1B2559',
                mb: 1,
                textAlign: 'left',
              }}
            >
              Items being moved
            </Typography>
            <div style={{ maxHeight: 'min(280px, 40vh)', overflowY: 'auto', border: '1px solid #E5E7EB', borderRadius: 6 }}>
              {selectedItems.map((item, idx) => (
                <div
                  key={item.id}
                  style={{
                    padding: '10px 12px',
                    borderBottom: idx === selectedItems.length - 1 ? 'none' : '1px solid #E5E7EB',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    fontSize: 13,
                    color: '#1F2937',
                  }}
                >
                  <span style={{ fontWeight: 600, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                    {item.sku}
                  </span>
                  <span style={{ color: '#7E8A90', flexShrink: 0, marginLeft: 8 }}>
                    Qty: {item.work_order_items_v2s?.[0]?.ship_quantity || 0}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </ModalWrapper>
    </Modal>
  );
};

export default MoveItemsModal;
